import React, { useState, useEffect } from 'react';
import { Settings, Zap, Code, Brain, Bug, FileText, Loader2, AlertCircle, Shield } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { apiService } from '../../../services/api';

import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { CursorSuccessModalContent } from './CursorSuccessModal';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface CursorIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (integration: any) => void;
  socket?: SpaceSocket;
  spaceId: string;
}

/** Cursor-only feature toggles; core memory ACL lives in separate state + binding permissions. */
interface CursorFeaturePermissions {
  code_sessions: boolean;
  ai_conversations: boolean;
  debugging_insights: boolean;
  project_context: boolean;
}

interface CursorConfig {
  permissions: CursorFeaturePermissions;
  max_content_length: number;
  auto_capture_sessions: boolean;
  session_timeout_minutes: number;
  capture_ai_interactions: boolean;
  project_context_depth: number;
}

/** Maps binding hourly rate to Cursor auth_config max_requests_per_minute (1–300). */
function derivedMaxRequestsPerMinute(requestsPerHour: number): number {
  return Math.min(300, Math.max(1, Math.round(requestsPerHour / 60)));
}

const defaultConfig = (): CursorConfig => ({
  permissions: {
    code_sessions: true,
    ai_conversations: true,
    debugging_insights: true,
    project_context: true,
  },
  max_content_length: 8192,
  auto_capture_sessions: true,
  session_timeout_minutes: 30,
  capture_ai_interactions: true,
  project_context_depth: 3,
});

const CursorIntegrationModal: React.FC<CursorIntegrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  socket,
  spaceId,
}) => {
  const { t } = useTranslation('integrations');
  const [isCreating, setIsCreating] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);
  const [requestsPerHour, setRequestsPerHour] = useState(7200);

  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.cursor);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      void loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  const [integrationName, setIntegrationName] = useState('My Cursor IDE');
  const [config, setConfig] = useState<CursorConfig>(() => defaultConfig());

  const [errors, setErrors] = useState<string[]>([]);

  const validateConfig = (): boolean => {
    const newErrors: string[] = [];

    if (!integrationName.trim()) {
      newErrors.push('Integration name is required');
    }

    if (config.max_content_length < 100 || config.max_content_length > 32768) {
      newErrors.push('Content length must be between 100 and 32,768 characters');
    }

    if (config.session_timeout_minutes < 5 || config.session_timeout_minutes > 120) {
      newErrors.push('Session timeout must be between 5 and 120 minutes');
    }

    if (config.project_context_depth < 1 || config.project_context_depth > 10) {
      newErrors.push('Project context depth must be between 1 and 10');
    }

    if (!canRead && !canWrite) {
      newErrors.push('At least read or write permission must be enabled');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateConfig() || !selectedSocket) {
      return;
    }

    const max_requests_per_minute = derivedMaxRequestsPerMinute(requestsPerHour);

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        requestsPerHour,
      });

      const fullAuthPermissions = {
        read: canRead,
        write: canWrite,
        search: canSearch,
        delete: canDelete,
        ...config.permissions,
      };

      const integrationData = {
        integration_type: 'cursor',
        integration_name: integrationName,
        permissions,
        auth_config: {
          max_content_length: config.max_content_length,
          auto_capture_sessions: config.auto_capture_sessions,
          session_timeout_minutes: config.session_timeout_minutes,
          capture_ai_interactions: config.capture_ai_interactions,
          project_context_depth: config.project_context_depth,
          permissions: fullAuthPermissions,
          max_requests_per_minute,
          cursor_version: 'latest',
          mcp_protocol: 'streamable-http',
          coding_features: {
            auto_capture: config.auto_capture_sessions,
            ai_interactions: config.capture_ai_interactions,
            session_management: true,
            project_context: config.permissions.project_context,
          },
        },
      };

      const integration = await apiService.createIntegrationBinding(
        selectedSocket.socket_id,
        integrationData
      );

      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: integration.binding_id,
        auth_config: {
          api_key: integration.auth_config?.api_key,
          max_content_length: config.max_content_length,
          auto_capture_sessions: config.auto_capture_sessions,
          session_timeout_minutes: config.session_timeout_minutes,
          capture_ai_interactions: config.capture_ai_interactions,
          project_context_depth: config.project_context_depth,
          permissions: fullAuthPermissions,
          max_requests_per_minute,
        },
        socket_id: selectedSocket.socket_id,
        metadata: {
          cursor_version: integrationData.auth_config.cursor_version,
          mcp_protocol: integrationData.auth_config.mcp_protocol,
          coding_features: integrationData.auth_config.coding_features,
        },
      });
      setShowSuccessModal(true);

      onSuccess(integration);
    } catch (error: unknown) {
      console.error('Error creating Cursor integration:', error);
      const message = error instanceof Error ? error.message : 'Failed to create Cursor integration. Please try again.';
      alert(`Setup Failed\n\n${message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);

    setIntegrationName('My Cursor IDE');
    setConfig(defaultConfig());
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setCategories('');
    setExcludeTags('');
    setMinImportance(1);
    setRequestsPerHour(7200);
    setErrors([]);

    onClose();
  };

  const updatePermission = (key: keyof CursorFeaturePermissions, value: boolean) => {
    setConfig((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value,
      },
    }));
  };

  const updateConfig = (key: keyof CursorConfig, value: string | number | boolean) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        variant="scrollable"
        className={
          showSuccessModal && createdIntegration
            ? 'max-w-5xl dark:bg-gray-800'
            : 'max-w-2xl dark:bg-gray-800'
        }
      >
        {showSuccessModal && createdIntegration ? (
          <CursorSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 border-b border-gray-200 p-6 pb-4 pr-12 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 p-2">
                  <Code className="h-6 w-6 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-xl text-gray-900 dark:text-white">
                    Setup Cursor IDE Integration
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
                    Connect your AI-powered code editor with memory system
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogScrollArea className="space-y-6 p-6">
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1">
                      {errors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="integrationName">Integration Name</Label>
                <Input
                  id="integrationName"
                  value={integrationName}
                  onChange={(e) => setIntegrationName(e.target.value)}
                  placeholder="My Cursor IDE"
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Give this integration a memorable name for your coding workflow
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {t('cursorIntegration.policy.permissions.title')}
                  </CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('cursorIntegration.policy.permissions.description')}
                  </p>
                </CardHeader>
                <CardContent>
                  <MemoryPermissionsFields
                    variant="embedded"
                    value={{
                      can_read: canRead,
                      can_write: canWrite,
                      can_search: canSearch,
                      can_delete: canDelete,
                    }}
                    onChange={(v) => {
                      setCanRead(v.can_read);
                      setCanWrite(v.can_write);
                      setCanSearch(v.can_search);
                      setCanDelete(v.can_delete);
                    }}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {t('cursorIntegration.policy.contentFiltering.title')}
                  </CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('cursorIntegration.policy.contentFiltering.description')}
                  </p>
                </CardHeader>
                <CardContent>
                  <ContentFiltersFields
                    variant="embedded"
                    categories={categories}
                    excludeTags={excludeTags}
                    minImportance={minImportance}
                    onCategoriesChange={setCategories}
                    onExcludeTagsChange={setExcludeTags}
                    onMinImportanceChange={setMinImportance}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    {t('cursorIntegration.policy.rateLimiting.title')}
                  </CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('cursorIntegration.policy.rateLimiting.description')}
                  </p>
                </CardHeader>
                <CardContent>
                  <RateLimitFields
                    requestsPerHour={requestsPerHour}
                    onRequestsPerHourChange={setRequestsPerHour}
                    min={1}
                    max={100000}
                  />
                </CardContent>
              </Card>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Brain className="h-4 w-4 text-blue-500" />
                  <Label className="text-base font-medium">Cursor IDE Features</Label>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Code className="h-4 w-4 text-blue-500" />
                      <div>
                        <Label htmlFor="code_sessions" className="text-sm font-medium">
                          Coding Sessions
                        </Label>
                        <p className="text-xs text-gray-500">Capture code workflows</p>
                      </div>
                    </div>
                    <Switch
                      id="code_sessions"
                      checked={config.permissions.code_sessions}
                      onCheckedChange={(checked) => updatePermission('code_sessions', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Brain className="h-4 w-4 text-purple-500" />
                      <div>
                        <Label htmlFor="ai_conversations" className="text-sm font-medium">
                          AI Conversations
                        </Label>
                        <p className="text-xs text-gray-500">Store AI chat history</p>
                      </div>
                    </div>
                    <Switch
                      id="ai_conversations"
                      checked={config.permissions.ai_conversations}
                      onCheckedChange={(checked) => updatePermission('ai_conversations', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Bug className="h-4 w-4 text-red-500" />
                      <div>
                        <Label htmlFor="debugging_insights" className="text-sm font-medium">
                          Debug Insights
                        </Label>
                        <p className="text-xs text-gray-500">Save debugging discoveries</p>
                      </div>
                    </div>
                    <Switch
                      id="debugging_insights"
                      checked={config.permissions.debugging_insights}
                      onCheckedChange={(checked) => updatePermission('debugging_insights', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-green-500" />
                      <div>
                        <Label htmlFor="project_context" className="text-sm font-medium">
                          Project Context
                        </Label>
                        <p className="text-xs text-gray-500">Track project memories</p>
                      </div>
                    </div>
                    <Switch
                      id="project_context"
                      checked={config.permissions.project_context}
                      onCheckedChange={(checked) => updatePermission('project_context', checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <Label className="text-base font-medium">Performance Settings</Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contentLength">Max Content Length</Label>
                    <Input
                      id="contentLength"
                      type="number"
                      min="100"
                      max="32768"
                      value={config.max_content_length}
                      onChange={(e) =>
                        updateConfig('max_content_length', parseInt(e.target.value, 10) || 8192)
                      }
                    />
                    <p className="text-xs text-gray-500">Characters for code snippets</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sessionTimeout">Session Timeout (min)</Label>
                    <Input
                      id="sessionTimeout"
                      type="number"
                      min="5"
                      max="120"
                      value={config.session_timeout_minutes}
                      onChange={(e) =>
                        updateConfig('session_timeout_minutes', parseInt(e.target.value, 10) || 30)
                      }
                    />
                    <p className="text-xs text-gray-500">Coding session duration</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contextDepth">Project Context Depth</Label>
                    <Input
                      id="contextDepth"
                      type="number"
                      min="1"
                      max="10"
                      value={config.project_context_depth}
                      onChange={(e) =>
                        updateConfig('project_context_depth', parseInt(e.target.value, 10) || 3)
                      }
                    />
                    <p className="text-xs text-gray-500">Memory context levels</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-base font-medium">Auto-Capture Settings</Label>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div>
                    <Label htmlFor="autoCapture" className="text-sm font-medium">
                      Auto-Capture Sessions
                    </Label>
                    <p className="text-xs text-gray-500">Automatically save coding sessions</p>
                  </div>
                  <Switch
                    id="autoCapture"
                    checked={config.auto_capture_sessions}
                    onCheckedChange={(checked) => updateConfig('auto_capture_sessions', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div>
                    <Label htmlFor="captureAI" className="text-sm font-medium">
                      Capture AI Interactions
                    </Label>
                    <p className="text-xs text-gray-500">Save AI chat conversations automatically</p>
                  </div>
                  <Switch
                    id="captureAI"
                    checked={config.capture_ai_interactions}
                    onCheckedChange={(checked) => updateConfig('capture_ai_interactions', checked)}
                  />
                </div>
              </div>

              {socketLoading && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="text-sm text-blue-800">Setting up socket...</p>
                  </div>
                </div>
              )}

              {socketError && !socketLoading && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Failed to load socket</p>
                      <p className="text-xs text-red-700 mt-1">{socketError}</p>
                      <p className="text-xs text-red-700 mt-1">
                        You can create a socket manually in the advanced section below.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedSocket && !socketLoading && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge variant="secondary">
                      Socket: {selectedSocket.socket_config?.template_id?.toUpperCase() || 'API'}
                    </Badge>
                    <Badge variant="outline">{selectedSocket.socket_name}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This integration will use your <strong>{selectedSocket.socket_name}</strong> socket
                    for Cursor IDE connectivity.
                  </p>
                </div>
              )}

              {spaceId && (
                <div className="mt-4">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.cursor}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || socketLoading}
                  />
                </div>
              )}

              </DialogScrollArea>
              <DialogFooter variant="scrollable" className="gap-3 border-gray-200 px-6 pb-6 dark:border-gray-700">
                <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating || !selectedSocket || socketLoading}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  {isCreating ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Creating...</span>
                    </div>
                  ) : (
                    'Create Cursor Integration'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CursorIntegrationModal;
